import React, { useEffect, useState } from 'react';
import { TagTypeahead } from '../../../components/TagTypeahead';
import { type Tag } from '../../../hooks/useTags';
import { type LoadFileFormat } from '../../../hooks/useLoadFileFormats';
import { COLOR_SETTING, OUTPUT_TYPE, type ExportFormState } from '../../../types/export';
import { useProjectIdFromRoute } from '../../../hooks/useProjectIdFromRoute';

import type { RelativitySettingsItem } from '../../../types/relativitySettings';

export type ExportDestination = 'ClientStorage' | 'DownloadLink' | 'Relativity';

interface Props {
  formState: ExportFormState;
  onChange: (patch: Partial<ExportFormState>) => void;
  nativeEnabled: boolean;
  nativeType: 'Native' | 'NativeWithPlaceholder';
  onNativeEnabledChange: (v: boolean) => void;
  onNativeTypeChange: (v: 'Native' | 'NativeWithPlaceholder') => void;
  loadFileFormats: LoadFileFormat[];
  onEditLoadFileFormat: (format?: LoadFileFormat) => void;
  onValidChange: (valid: boolean) => void;
  relativitySettingsList?: RelativitySettingsItem[];
  onEditRelativitySettings?: (settings?: RelativitySettingsItem) => void;
  /** Client Storage is only offered to projects in an Organization; hidden otherwise. */
  clientStorageEnabled?: boolean;
  /** Direct to Relativity is org- + user/flag-gated; offered as a third destination. */
  directToRelativityEnabled?: boolean;
  onDestinationChange: (dest: ExportDestination) => void;
}

export function Step2Output({
  formState,
  onChange,
  nativeEnabled,
  nativeType,
  onNativeEnabledChange,
  onNativeTypeChange,
  loadFileFormats,
  onEditLoadFileFormat,
  onValidChange,
  relativitySettingsList = [],
  onEditRelativitySettings,
  clientStorageEnabled = false,
  directToRelativityEnabled = false,
  onDestinationChange,
}: Props) {
  const directToRelativity = formState.directToRelativity;
  const destination: ExportDestination = directToRelativity
    ? 'Relativity'
    : formState.clientStorageUpload ? 'ClientStorage' : 'DownloadLink';
  // Only show the picker when there's a real choice (org enables Client Storage /
  // Relativity); otherwise the export always uses a download link.
  const showDestinationSelector = clientStorageEnabled || directToRelativityEnabled;
  const projectId = useProjectIdFromRoute();
  const hasPdf = formState.outputTypes.includes(OUTPUT_TYPE.Pdf);
  const hasTiff = formState.outputTypes.includes(OUTPUT_TYPE.Tiff);
  const imageEnabled = hasPdf || hasTiff;

  // "With Placeholders" radio is disabled when no output type besides Text/Native is selected
  const withPlaceholderDisabled =
    !nativeEnabled ||
    formState.outputTypes.filter(
      t => t !== OUTPUT_TYPE.Text && t !== OUTPUT_TYPE.Native && t !== OUTPUT_TYPE.NativeWithPlaceholder,
    ).length === 0;

  function toggleOutputType(type: string, checked: boolean) {
    const types = checked
      ? [...formState.outputTypes, type]
      : formState.outputTypes.filter(t => t !== type);
    onChange({ outputTypes: types });
  }

  // Emit validity
  useEffect(() => {
    const valid = directToRelativity
      ? !!formState.relativitySettingsId
      : formState.loadFileFormatIds.length > 0 && (formState.outputTypes.length > 0 || nativeEnabled);
    onValidChange(valid);
  }, [directToRelativity, formState.relativitySettingsId, formState.loadFileFormatIds, formState.outputTypes, nativeEnabled, onValidChange]);

  const showJpgCheckbox =
    hasTiff &&
    (formState.colorSetting === COLOR_SETTING.Color ||
      formState.colorSetting === COLOR_SETTING.ColorTaggedOnly);

  const [placeholderTag, setPlaceholderTag] = useState<Tag | null>(
    formState.tagPlaceholder ? { ...formState.tagPlaceholder, projectId: formState.projectId, color: '' } : null,
  );

  const relativityBlock = (
    <div className="row">
      <div className="col-md-12">
        <h5>Relativity Settings</h5>
        <div className="well well-small" style={{ backgroundColor: '#fff', overflow: 'auto' }}>
          <p>
            <a onClick={() => onEditRelativitySettings?.()} style={{ cursor: 'pointer' }}>
              Create new relativity settings
            </a>
            .
          </p>
          {relativitySettingsList.map(s => (
            <div key={s.id} className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="relativitySettings"
                id={`rs-${s.id}`}
                checked={formState.relativitySettingsId === s.id}
                onChange={() => onChange({ relativitySettingsId: s.id })}
              />
              <label className="form-check-label" htmlFor={`rs-${s.id}`}>
                {s.name}{' '}
                <a onClick={() => onEditRelativitySettings?.(s)} style={{ cursor: 'pointer' }}>
                  edit
                </a>
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Unified export destination picker — sits at the top of Step 2. Direct to Relativity
  // is the third option, gated the same as before (org + user/flag).
  const destinationSelector = showDestinationSelector ? (
    <div className="row">
      <div className="col-md-12">
        <h5>Output Location</h5>
        <div className="well well-small" style={{ backgroundColor: '#fff', overflow: 'auto' }}>
          {clientStorageEnabled && (
            <div className="radio radio-primary" style={{ marginLeft: 25 }}>
              <input
                id="outputToStorage"
                type="radio"
                checked={destination === 'ClientStorage'}
                onChange={() => onDestinationChange('ClientStorage')}
              />
              <label htmlFor="outputToStorage">Save to Client Storage</label>
            </div>
          )}
          <div className="radio radio-primary" style={{ marginLeft: 25 }}>
            <input
              id="outputToLink"
              type="radio"
              checked={destination === 'DownloadLink'}
              onChange={() => onDestinationChange('DownloadLink')}
            />
            <label htmlFor="outputToLink">Direct Download Link</label>
          </div>
          {directToRelativityEnabled && (
            <div className="radio radio-primary" style={{ marginLeft: 25 }}>
              <input
                id="outputToRelativity"
                type="radio"
                checked={destination === 'Relativity'}
                onChange={() => onDestinationChange('Relativity')}
              />
              <label htmlFor="outputToRelativity">Direct to Relativity</label>
            </div>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const downloadOptionsBlock = (
    <>
      <div className="row">
        {/* Include — left column */}
        <div className="col-md-6">
          <h5>Include</h5>
          <div className="well well-small" style={{ backgroundColor: '#fff', minHeight: 316 }}>
            <b>Billed Per Page</b>
            <div className="checkbox checkbox-primary">
              <input
                id="outputPdf"
                type="checkbox"
                checked={formState.outputTypes.includes(OUTPUT_TYPE.Pdf)}
                onChange={e => toggleOutputType(OUTPUT_TYPE.Pdf, e.target.checked)}
              />
              <label htmlFor="outputPdf">PDFs</label>
            </div>
            <div className="checkbox checkbox-primary">
              <input
                id="outputTiff"
                type="checkbox"
                checked={formState.outputTypes.includes(OUTPUT_TYPE.Tiff)}
                onChange={e => toggleOutputType(OUTPUT_TYPE.Tiff, e.target.checked)}
              />
              <label htmlFor="outputTiff">TIFFs (Includes OPT)</label>
            </div>
            <br />
            <b>Billed Per GB</b>
            <div className="checkbox checkbox-primary">
              <input
                id="outputText"
                type="checkbox"
                checked={formState.outputTypes.includes(OUTPUT_TYPE.Text)}
                onChange={e => toggleOutputType(OUTPUT_TYPE.Text, e.target.checked)}
              />
              <label htmlFor="outputText">Text</label>
            </div>
            <div className="checkbox checkbox-primary">
              <input
                id="outputNativePdf"
                type="checkbox"
                checked={formState.outputTypes.includes(OUTPUT_TYPE.NativePdf)}
                onChange={e => toggleOutputType(OUTPUT_TYPE.NativePdf, e.target.checked)}
              />
              <label htmlFor="outputNativePdf">Native PDFs</label>
            </div>
            <div className="checkbox checkbox-primary">
              <input
                id="outputNatives"
                type="checkbox"
                checked={nativeEnabled}
                onChange={e => onNativeEnabledChange(e.target.checked)}
              />
              <label htmlFor="outputNatives">Natives</label>
            </div>
            <div className="radio radio-primary" style={{ marginLeft: 25 }}>
              <input
                id="nativeAlways"
                type="radio"
                disabled={!nativeEnabled}
                checked={nativeType === 'Native'}
                onChange={() => onNativeTypeChange('Native')}
              />
              <label htmlFor="nativeAlways">Always</label>
            </div>
            <div className="radio radio-primary" style={{ marginLeft: 25 }}>
              <input
                id="nativeWithPlaceholder"
                type="radio"
                disabled={withPlaceholderDisabled}
                checked={nativeType === 'NativeWithPlaceholder'}
                onChange={() => onNativeTypeChange('NativeWithPlaceholder')}
              />
              <label htmlFor="nativeWithPlaceholder">With Placeholders</label>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="col-md-6">
          <h5>Page Orientation</h5>
          <div className="well well-small" style={{ backgroundColor: '#fff', overflow: 'auto' }}>
            <div className="checkbox checkbox-primary">
              <input
                id="keepPageOrientation"
                type="checkbox"
                disabled={!imageEnabled}
                checked={formState.keepPageOrientation}
                onChange={e => onChange({ keepPageOrientation: e.target.checked })}
              />
              <label htmlFor="keepPageOrientation">Keep Original Page Orientation</label>
            </div>
          </div>

          <h5>Color Settings</h5>
          <div className="well well-small" style={{ backgroundColor: '#fff', overflow: 'auto', height: 199 }}>
            <div className="radio radio-primary">
              <input
                id="colorBW"
                type="radio"
                name="colorSettings"
                disabled={!imageEnabled}
                checked={formState.colorSetting === COLOR_SETTING.BlackAndWhite}
                onChange={() => onChange({ colorSetting: COLOR_SETTING.BlackAndWhite })}
              />
              <label htmlFor="colorBW">Black &amp; White</label>
            </div>
            <div className="radio radio-primary">
              <input
                id="colorAll"
                type="radio"
                name="colorSettings"
                disabled={!imageEnabled}
                checked={formState.colorSetting === COLOR_SETTING.Color}
                onChange={() => onChange({ colorSetting: COLOR_SETTING.Color })}
              />
              <label htmlFor="colorAll">Full Color</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: 155, flexShrink: 0 }}>
                <div className="radio radio-primary">
                  <input
                    id="colorTagged"
                    type="radio"
                    name="colorSettings"
                    disabled={!imageEnabled}
                    checked={formState.colorSetting === COLOR_SETTING.ColorTaggedOnly}
                    onChange={() => onChange({ colorSetting: COLOR_SETTING.ColorTaggedOnly })}
                  />
                  <label htmlFor="colorTagged">Full Color for Tag</label>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                {imageEnabled ? (
                  <TagTypeahead
                    projectId={projectId}
                    selected={formState.colorTag ? [formState.colorTag] : []}
                    onChange={tags => onChange({ colorTag: tags[0] ?? null })}
                    placeholder="Tag name..."
                  />
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    disabled
                    value={formState.colorTag?.name ?? ''}
                  />
                )}
              </div>
            </div>
            {showJpgCheckbox && (
              <div style={{ float: 'left' }}>
                <div className="checkbox checkbox-primary">
                  <input
                    id="tiffExportColorFileFormat"
                    type="checkbox"
                    checked={formState.tiffExportColorFileFormat === 'JPG'}
                    onChange={e => onChange({ tiffExportColorFileFormat: e.target.checked ? 'JPG' : 'TIF' })}
                  />
                  <label htmlFor="tiffExportColorFileFormat">Use JPG Instead of TIF</label>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Placeholder */}
      <div className="row">
        <div className="col-md-12">
          <h5>Placeholder</h5>
          <div className="well well-small" style={{ backgroundColor: '#fff', overflow: 'auto' }}>
            <div className="checkbox checkbox-primary">
              <input id="missingImages" type="checkbox" checked disabled readOnly />
              <label htmlFor="missingImages">Documents that Failed to Generate Valid Image</label>
            </div>
            <div className="checkbox checkbox-primary">
              <input
                id="spreadsheetPlaceholder"
                type="checkbox"
                checked={formState.spreadsheetPlaceholder}
                onChange={e => onChange({ spreadsheetPlaceholder: e.target.checked })}
              />
              <label htmlFor="spreadsheetPlaceholder">For Spreadsheets</label>
            </div>
            <div className="checkbox checkbox-primary">
              <input
                id="presentationPlaceholder"
                type="checkbox"
                checked={formState.presentationPlaceholder}
                onChange={e => onChange({ presentationPlaceholder: e.target.checked })}
              />
              <label htmlFor="presentationPlaceholder">Presentations</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: 217, flexShrink: 0 }}>
                <div className="checkbox checkbox-primary">
                  <input
                    id="tagPlaceholderEnabled"
                    type="checkbox"
                    checked={formState.tagPlaceholderEnabled}
                    onChange={e => onChange({ tagPlaceholderEnabled: e.target.checked })}
                  />
                  <label htmlFor="tagPlaceholderEnabled">Documents Tagged With</label>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                {formState.tagPlaceholderEnabled ? (
                  <TagTypeahead
                    projectId={projectId}
                    selected={placeholderTag ? [placeholderTag] : []}
                    onChange={tags => {
                      const t = tags[0] ?? null;
                      setPlaceholderTag(t);
                      onChange({ tagPlaceholder: t ? { id: t.id, name: t.name } : null });
                    }}
                    placeholder="Tag name..."
                  />
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    disabled
                    value={placeholderTag?.name ?? ''}
                  />
                )}
              </div>
            </div>

            <div style={{ paddingTop: 15 }} className="row">
              <div className="col-sm-4">
                <label htmlFor="virusPlaceholderText" className="control-label">Virus Placeholder Text</label>
              </div>
              <div className="col-sm-8">
                <input
                  id="virusPlaceholderText"
                  type="text"
                  className="form-control"
                  maxLength={255}
                  required
                  value={formState.virusPlaceholderText}
                  onChange={e => onChange({ virusPlaceholderText: e.target.value })}
                />
              </div>
            </div>

            <div className="row" style={{ paddingTop: 10 }}>
              <div className="col-sm-4">
                <label htmlFor="nativeFormatPlaceholderText" className="control-label">
                  Native Format Placeholder Text
                </label>
              </div>
              <div className="col-sm-8">
                <input
                  id="nativeFormatPlaceholderText"
                  type="text"
                  className="form-control"
                  maxLength={255}
                  required
                  value={formState.nativeFormatPlaceholderText}
                  onChange={e => onChange({ nativeFormatPlaceholderText: e.target.value })}
                />
              </div>
            </div>

            <div className="row" style={{ paddingTop: 10 }}>
              <div className="col-sm-4">
                <label htmlFor="failedToImagePlaceholderText" className="control-label">
                  Failed to Image Placeholder Text
                </label>
              </div>
              <div className="col-sm-8">
                <input
                  id="failedToImagePlaceholderText"
                  type="text"
                  className="form-control"
                  maxLength={255}
                  required
                  value={formState.failedToImagePlaceholderText}
                  onChange={e => onChange({ failedToImagePlaceholderText: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Load Files */}
      <div className="row">
        <div className="col-md-12">
          <h5>Load Files</h5>
          <div className="well well-small" style={{ backgroundColor: '#fff', overflow: 'auto' }}>
            <p>
              Choose from our list of default load file formats, or{' '}
              <a onClick={() => onEditLoadFileFormat()} style={{ cursor: 'pointer' }}>
                create your own custom format
              </a>
              .
            </p>
            {loadFileFormats.map(format => (
              <div key={format.id} className="checkbox checkbox-primary">
                <input
                  id={`lff-${format.id}`}
                  type="checkbox"
                  checked={formState.loadFileFormatIds.includes(format.id)}
                  onChange={e => {
                    const ids = e.target.checked
                      ? [...formState.loadFileFormatIds, format.id]
                      : formState.loadFileFormatIds.filter(x => x !== format.id);
                    const names = loadFileFormats.filter(f => ids.includes(f.id ?? '')).map(f => f.name);
                    onChange({ loadFileFormatIds: ids, loadFileFormatNames: names });
                  }}
                />
                <label htmlFor={`lff-${format.id}`}>
                  {format.name}
                  {format.type === 'user' && (
                    <>
                      {' '}
                      <a onClick={() => onEditLoadFileFormat(format)} style={{ cursor: 'pointer' }}>
                        edit
                      </a>
                    </>
                  )}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div>
      {destinationSelector}
      {destination === 'Relativity' ? relativityBlock : downloadOptionsBlock}
    </div>
  );
}
